import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, ToggleField } from '../../../shared/components';
import { updateChannelAccount } from '../services/channelAccountService';
import { ChannelAccount, ChannelAccountChannel, ChannelAccountLimits } from '../types/channelAccount';
import styles from './ConnectorsPage.module.css';
import { ConnectorFieldStatus } from './ConnectorFieldStatus';
import { ChannelAccountLimitsEditor } from './ChannelAccountLimitsEditor';

interface ChannelAccountBarProps {
  channel: ChannelAccountChannel;
  accounts: ChannelAccount[];
  limits: ChannelAccountLimits | null;
  loading: boolean;
  error: string | null;
  selectedAccountId: string | null;
  onSelectAccount: (accountId: string) => void;
  onRefresh: () => void;
  canManage: boolean;
  showAddForm: boolean;
  onToggleAddForm: () => void;
  onAccountUpdated?: () => void;
  connectionKind?: 'cloud' | 'app';
}

const getUsedLimit = (
  channel: ChannelAccountChannel,
  limits: ChannelAccountLimits | null,
  accountCount: number,
  connectionKind?: 'cloud' | 'app',
): { used: number; max: number } => {
  if (!limits) {
    return { used: accountCount, max: 1 };
  }

  if (channel === 'WhatsApp' && connectionKind === 'app') {
    return { used: limits.usedWhatsAppApp, max: limits.maxWhatsAppApp };
  }

  if (channel === 'WhatsApp') {
    return { used: limits.usedWhatsApp, max: limits.maxWhatsApp };
  }

  if (channel === 'Instagram') {
    return { used: limits.usedInstagram, max: limits.maxInstagram };
  }

  if (channel === 'Telegram') {
    return { used: limits.usedTelegram, max: limits.maxTelegram };
  }

  if (channel === 'Messenger') {
    return { used: limits.usedMessenger, max: limits.maxMessenger };
  }

  if (channel === 'TikTok') {
    return { used: limits.usedTikTok, max: limits.maxTikTok };
  }

  if (channel === 'Voice') {
    return { used: limits.usedVoice, max: limits.maxVoice };
  }

  // SMS has no per-channel cap; it counts against the workspace total.
  if (channel === 'Sms') {
    return { used: accountCount, max: limits.maxTotal };
  }

  return { used: limits.usedWebchat, max: limits.maxWebchat };
};

export const ChannelAccountBar: React.FC<ChannelAccountBarProps> = ({
  channel,
  accounts,
  limits,
  loading,
  error,
  selectedAccountId,
  onSelectAccount,
  onRefresh,
  canManage,
  showAddForm,
  onToggleAddForm,
  onAccountUpdated,
  connectionKind,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const selectedAccount = accounts.find(account => account.id === selectedAccountId) ?? null;
  const { used, max } = getUsedLimit(channel, limits, accounts.length, connectionKind);
  const canAddAccount = canManage && used < max;

  const [displayName, setDisplayName] = React.useState('');
  const [isEnabled, setIsEnabled] = React.useState(true);
  const [isDefault, setIsDefault] = React.useState(false);
  const [savingMeta, setSavingMeta] = React.useState(false);
  const [metaError, setMetaError] = React.useState<string | null>(null);
  const [metaNotice, setMetaNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!selectedAccount) {
      setDisplayName('');
      setIsEnabled(true);
      setIsDefault(false);
      return;
    }

    setDisplayName(selectedAccount.displayName);
    setIsEnabled(selectedAccount.isEnabled);
    setIsDefault(selectedAccount.isDefault);
    setMetaError(null);
    setMetaNotice(null);
  }, [selectedAccount]);

  const hasMetaChanges = selectedAccount
    ? displayName.trim() !== selectedAccount.displayName
      || isEnabled !== selectedAccount.isEnabled
      || isDefault !== selectedAccount.isDefault
    : false;

  const saveMeta = async () => {
    if (!selectedAccount || !displayName.trim()) {
      return;
    }

    setSavingMeta(true);
    setMetaError(null);
    setMetaNotice(null);

    try {
      await updateChannelAccount(selectedAccount.id, {
        displayName: displayName.trim(),
        isEnabled,
        isDefault,
      });
      setMetaNotice(t('channelAccountMetaSaved', { ns: 'companies' }));
      onAccountUpdated?.();
      await onRefresh();
    } catch (saveError) {
      setMetaError(saveError instanceof Error ? saveError.message : t('failedToSaveChannelAccountMeta', { ns: 'companies' }));
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <div className={styles.channelAccountSection}>
      <ChannelAccountLimitsEditor
        showEditor={accounts.length > 0}
        onUpdated={onRefresh}
      />
      <div className={styles.channelAccountBar}>
        <div className={styles.channelAccountBarHead}>
          <h4>{t('channelAccountsHeading', { ns: 'companies' })}</h4>
          <span className={styles.channelAccountLimits}>
            {t('channelAccountLimits', { ns: 'companies', used, max })}
          </span>
        </div>

        {error ? <div className="error">{error}</div> : null}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
            <Spinner text={t('loadingChannelAccounts', { ns: 'companies' })} />
          </div>
        ) : (
          <>
            <div className={styles.channelAccountControls}>
              <label className={styles.channelAccountSelectLabel}>
                {t('channelAccountSelectLabel', { ns: 'companies' })}
                <select
                  className="text-input"
                  value={selectedAccountId ?? ''}
                  onChange={event => onSelectAccount(event.target.value)}
                  disabled={accounts.length === 0}
                >
                  {accounts.length === 0 ? (
                    <option value="">{t('channelAccountNone', { ns: 'companies' })}</option>
                  ) : (
                    accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.displayName || account.externalAccountId}
                        {account.isDefault ? ` (${t('channelAccountDefault', { ns: 'companies' })})` : ''}
                        {!account.isEnabled ? ` (${t('channelAccountDisabled', { ns: 'companies' })})` : ''}
                      </option>
                    ))
                  )}
                </select>
              </label>

              {canAddAccount ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onToggleAddForm}
                  disabled={!canManage}
                >
                  {showAddForm
                    ? t('channelAccountCancelAdd', { ns: 'companies' })
                    : t('channelAccountAdd', { ns: 'companies', channel })}
                </button>
              ) : null}
            </div>

            {selectedAccount ? (
              <div className={styles.channelAccountMeta}>
                <div className={styles.channelAccountMetaGrid}>
                  <label>
                    {t('channelAccountDisplayName', { ns: 'companies' })}
                    <input
                      className="text-input"
                      value={displayName}
                      onChange={event => setDisplayName(event.target.value)}
                      disabled={!canManage || savingMeta}
                    />
                  </label>

                  <label>
                    {t('channelAccountExternalId', { ns: 'companies' })}
                    <input className="text-input" value={selectedAccount.externalAccountId} readOnly />
                  </label>

                  <ToggleField
                    label={t('channelAccountEnabled', { ns: 'companies' })}
                    checked={isEnabled}
                    onChange={setIsEnabled}
                    disabled={!canManage || savingMeta}
                  />

                  <ToggleField
                    label={t('channelAccountDefaultLabel', { ns: 'companies' })}
                    checked={isDefault}
                    onChange={setIsDefault}
                    disabled={!canManage || savingMeta}
                  />
                </div>

                {metaError ? <div className="error">{metaError}</div> : null}
                {metaNotice ? <div className="success">{metaNotice}</div> : null}

                {canManage ? (
                  <div className="company-actions-inline" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => { void saveMeta(); }}
                      disabled={savingMeta || !hasMetaChanges || !displayName.trim()}
                    >
                      {savingMeta ? t('savingEllipsis', { ns: 'admin' }) : t('channelAccountSaveMeta', { ns: 'companies' })}
                    </button>
                  </div>
                ) : null}

                {selectedAccount.credentialStatus && (channel === 'WhatsApp' || channel === 'Instagram' || channel === 'Telegram' || channel === 'Messenger' || channel === 'TikTok') ? (
                  <div className={styles.channelAccountCredentialStatus}>
                    <div className={styles.channelAccountCredentialStatusTitle}>
                      {t('channelAccountCredentialStatusHeading', { ns: 'companies' })}
                    </div>
                    <dl className={styles.channelAccountCredentialStatusList}>
                      <div>
                        <dt>
                          {channel === 'WhatsApp'
                            ? t('whatsappStatusAccessToken', { ns: 'companies' })
                            : channel === 'Instagram'
                              ? t('instagramAccessToken', { ns: 'companies' })
                              : channel === 'Telegram'
                                ? t('telegramStatusBotToken', { ns: 'companies' })
                                : channel === 'Messenger'
                                  ? t('messengerPageAccessToken', { ns: 'companies' })
                                  : t('tiktokAccessToken', { ns: 'companies' })}
                        </dt>
                        <dd><ConnectorFieldStatus done={selectedAccount.credentialStatus.hasAccessToken} /></dd>
                      </div>
                      {(channel === 'WhatsApp' || channel === 'Instagram' || channel === 'Telegram' || channel === 'Messenger') ? (
                        <div>
                          <dt>
                            {channel === 'WhatsApp'
                              ? t('whatsappStatusVerifyToken', { ns: 'companies' })
                              : channel === 'Instagram'
                                ? t('instagramWebhookVerifyToken', { ns: 'companies' })
                                : channel === 'Telegram'
                                  ? t('telegramStatusWebhookSecret', { ns: 'companies' })
                                  : t('messengerWebhookVerifyToken', { ns: 'companies' })}
                          </dt>
                          <dd><ConnectorFieldStatus done={selectedAccount.credentialStatus.hasWebhookVerifyToken} /></dd>
                        </div>
                      ) : null}
                      {(channel === 'WhatsApp' || channel === 'Messenger' || channel === 'TikTok') ? (
                        <div>
                          <dt>
                            {channel === 'WhatsApp'
                              ? t('whatsappAppSecret', { ns: 'companies' })
                              : channel === 'Messenger'
                                ? t('messengerAppSecret', { ns: 'companies' })
                                : t('tiktokAppSecret', { ns: 'companies' })}
                          </dt>
                          <dd><ConnectorFieldStatus done={selectedAccount.credentialStatus.hasAppSecret} /></dd>
                        </div>
                      ) : null}
                      {(channel === 'WhatsApp' || channel === 'Messenger' || channel === 'TikTok') ? (
                        <div>
                          <dt>
                            {channel === 'WhatsApp'
                              ? t('whatsappStatusSignatureValidation', { ns: 'companies' })
                              : channel === 'Messenger'
                                ? t('messengerValidateSignature', { ns: 'companies' })
                                : t('tiktokValidateSignature', { ns: 'companies' })}
                          </dt>
                          <dd><ConnectorFieldStatus done={selectedAccount.credentialStatus.validateSignatureEnabled} variant="enabled" /></dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
